import React, { useMemo } from "react";

import ItemStatus from "pianofunclub-crm/components/General/ItemStatus";
import type { EnrolmentItem } from "pianofunclub-crm/components/ListItems/EnrolmentSelectRow";
import { getEnrolmentWarningText } from "pianofunclub-crm/utils/extractors";

import type { CellProps } from "pianofunclub-shared/components/Base/Cell";
import Row from "pianofunclub-shared/components/Base/Row";
import { WarningIcon } from "pianofunclub-shared/components/Other/Icons";
import {
  schoolYearConverter,
  combineWaitingListNotes,
  getEnrolmentStatus,
} from "pianofunclub-shared/utils/converters";
import { getFullName } from "pianofunclub-shared/utils/extractors";

export interface WaitingListTableRowProps {
  cellProps?: CellProps;
  data: NonNullable<NonNullable<EnrolmentItem>["node"]>;
  flexArray: number[];
  onPressEnrolment: (enrolmentId: string) => void;
  onPressSchool: (schoolId: string | null | undefined) => void;
  onPressStatus: (enrolmentId: string, status: string, enrolmentSchoolIsArchived: boolean) => void;
  rowHeight?: number;
  tableBorderColor?: string;
  tableBorderWidth?: number;
}

const WaitingListTableRow = (props: WaitingListTableRowProps) => {
  const {
    cellProps,
    data,
    flexArray,
    onPressEnrolment,
    onPressSchool,
    onPressStatus,
    rowHeight,
    tableBorderColor,
    tableBorderWidth,
  } = props;

  const rowData = useMemo(() => {
    const status = getEnrolmentStatus({
      lastOfferedTimestamp: data.lastOfferedTimestamp ?? "",
      schoolArchived: data.school?.archived ?? false,
      status: data.status ?? "",
    });

    const notes = combineWaitingListNotes(data.pupilNotes, data.adminNotes);

    const warningText = getEnrolmentWarningText({
      hasInvalidParentPhoneNumber: data.hasInvalidParentPhoneNumber,
      potentialDuplicatePupils: data.potentialDuplicatePupils,
      potentialDuplicates: data.potentialDuplicates,
    });

    return [
      {
        data: data.positionInWaitingList ?? "",
        onPress: () => onPressEnrolment(data.id),
      },
      {
        data: getFullName(data.pupilFirstName, data.pupilLastName),
        icon: warningText ? <WarningIcon color="yellow.500" size="5" /> : undefined,
        onPress: () => onPressEnrolment(data.id),
        tooltipLabel: `${warningText ? `${warningText}\n\n` : ""}Parent Info:\n${[
          data.parentName,
          data.parentEmail,
          data.parentPhoneNumber
            ? `${data.parentPhoneNumber}${data?.hasInvalidParentPhoneNumber ? " ⚠️" : ""}`
            : "",
        ]
          .filter(Boolean)
          .join("\n")}${notes ? `\n\n${notes}` : ""}`,
      },
      {
        data: data.school?.name ?? "",
        onPress: () => onPressSchool(data.school?.id),
      },
      {
        data: schoolYearConverter(data.schoolYear),
      },
      {
        data: data.schoolClass ?? "",
      },
      {
        data: data.instrument ?? "",
      },
      {
        data: data.lessonStage == "-1" ? "Any Stage" : (data.lessonStage ?? ""),
      },
      {
        data: status ? (
          <ItemStatus
            isTransparent
            label={status === "WAITING_LIST" ? "Waiting" : undefined}
            status={status}
            textProps={{
              fontFamily: "Poppins-Regular",
            }}
          />
        ) : (
          ""
        ),
        onPress: () => onPressStatus(data.id, status, data.school?.archived ?? false),
      },
    ];
  }, [
    data.adminNotes,
    data.hasInvalidParentPhoneNumber,
    data.id,
    data.instrument,
    data.lastOfferedTimestamp,
    data.lessonStage,
    data.parentEmail,
    data.parentName,
    data.parentPhoneNumber,
    data.positionInWaitingList,
    data.potentialDuplicatePupils,
    data.potentialDuplicates,
    data.pupilFirstName,
    data.pupilLastName,
    data.pupilNotes,
    data.school?.archived,
    data.school?.id,
    data.school?.name,
    data.schoolClass,
    data.schoolYear,
    data.status,
    onPressEnrolment,
    onPressSchool,
    onPressStatus,
  ]);

  return (
    <Row
      cellProps={cellProps}
      data={rowData}
      flexArray={flexArray}
      rowHeight={rowHeight}
      rowIndex={0}
      tableBorderColor={tableBorderColor}
      tableBorderWidth={tableBorderWidth}
    />
  );
};

export default WaitingListTableRow;
